import React, { useState, useEffect, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';

/**
 * GOV-7 Option A — Tenant360 Industry-Profile tab (the ONLY per-tenant assign surface).
 * Owner-usable GOVERNED cascade: canonical Industry → Sub-Sector → applicable Profile → Preview → Apply.
 * Selectors are populated from the existing canonical authorities (Taxonomy: /taxonomy/industries +
 * /taxonomy/sub-sectors; Profiles: /superadmin/industry-profiles) — no invented IDs, no fuzzy legacy mapping,
 * no duplicate governance surface. The component holds NO profile-resolution/eligibility/coherence logic; it
 * consumes the canonical backend /assign/preview and /assign/apply verbatim. Legacy sector strings are shown
 * separately and never mutated. Generic for ANY active profile (no Movers/Clinic-specific branch).
 */
const Tenant360IndustryProfile = ({ api, tenantRef, sector, subSector }) => {
  const [industries, setIndustries] = useState([]);
  const [subSectors, setSubSectors] = useState([]);
  const [profiles, setProfiles] = useState([]);
  const [industryId, setIndustryId] = useState('');
  const [subSectorId, setSubSectorId] = useState('');
  const [targetProfileId, setTargetProfileId] = useState('');
  const [preview, setPreview] = useState(null);
  const [classification, setClassification] = useState(null);   // PERSISTED current state (db.business_tenants)
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        // SuperAdmin-mounted taxonomy reads (require_superadmin_session) — Tenant360's owner API sends the
        // SuperAdmin OTP session token, NOT a Firebase ID token, so the plain /taxonomy/* (Firebase-gated)
        // routes 401 for this caller. The taxonomy router is re-registered under /superadmin for exactly this.
        const [ind, sub, prof] = await Promise.all([
          api.get('/api/v1/superadmin/taxonomy/industries').catch(() => ({ data: [] })),
          api.get('/api/v1/superadmin/taxonomy/sub-sectors').catch(() => ({ data: [] })),
          api.get('/api/v1/superadmin/industry-profiles').catch(() => ({ data: { profiles: [] } })),
        ]);
        if (!alive) return;
        setIndustries(Array.isArray(ind.data) ? ind.data : (ind.data?.industries || []));
        setSubSectors(Array.isArray(sub.data) ? sub.data : (sub.data?.sub_sectors || []));
        setProfiles((prof.data?.profiles || []).filter((p) => p.lifecycle === 'active'));
      } catch (e) { if (alive) setErr('Failed to load taxonomy/profiles'); }
    })();
    return () => { alive = false; };
  }, [api]);

  // CURRENT classification comes from the canonical PERSISTED tenant state (db.business_tenants) via the
  // Industry-Profile authority — NOT from a preview. This is what makes the tab truthful on a fresh mount
  // / tab-return / reload, before any Preview is run. Read-only.
  const loadClassification = React.useCallback(async () => {
    try {
      const r = await api.get(`/api/v1/superadmin/industry-profiles/tenant/${encodeURIComponent(tenantRef)}/classification`);
      setClassification(r.data && r.data.tenant_found ? r.data : null);
    } catch (e) { /* leave null → legacy props still render; never blocks the tab */ }
  }, [api, tenantRef]);

  useEffect(() => { loadClassification(); }, [loadClassification]);

  const nameOf = (list, id) => (list.find((x) => x.id === id) || {}).name || id || '—';
  const reset = () => { setPreview(null); setResult(null); setErr(null); };

  // Cascade: sub-sectors filtered by the chosen canonical Industry; profiles filtered to those applicable to
  // the chosen Industry (+ sub-sector when set, else industry-wide). This is navigation only — the backend
  // remains the resolution authority.
  const subOptions = useMemo(() => subSectors.filter((s) => !industryId || s.industry_id === industryId), [subSectors, industryId]);
  const profileOptions = useMemo(() => profiles.filter((p) => {
    if (industryId && p.industry_id !== industryId) return false;
    if (subSectorId) { const subs = p.sub_sector_ids || []; if (subs.length && !subs.includes(subSectorId)) return false; }
    return true;
  }), [profiles, industryId, subSectorId]);

  const runPreview = async () => {
    reset();
    if (!targetProfileId) { setErr('Select an Industry, Sub-Sector and applicable Profile.'); return; }
    setBusy(true);
    try {
      const r = await api.post('/api/v1/superadmin/industry-profiles/assign/preview',
        { tenant_key: tenantRef, target_profile_id: targetProfileId });
      setPreview(r.data || null);
    } catch (e) { setErr(e?.response?.data?.detail || 'Preview failed'); }
    finally { setBusy(false); }
  };

  // A no-op (target profile+version already the persisted pin) is NOT a switch — the backend flags it and the
  // UI refuses Apply so repeated same-profile applies cannot fabricate profile-switch history.
  const noChange = !!(preview && preview.no_change);
  const canApply = !!(preview && preview.tenant_found && preview.target_profile_active && !noChange);

  const runApply = async () => {
    if (!canApply) return;
    setBusy(true); setErr(null);
    try {
      const r = await api.post('/api/v1/superadmin/industry-profiles/assign/apply',
        { tenant_key: tenantRef, target_profile_id: targetProfileId });
      setResult(r.data || null);
      await loadClassification();   // converge on persisted state immediately (no second Preview needed)
      // Clear the transient target/preview + selectors so the completed transition does NOT masquerade as a
      // still-pending Target after Apply. Current classification now reflects the new persisted state.
      setPreview(null); setTargetProfileId(''); setSubSectorId(''); setIndustryId('');
    } catch (e) { setErr(e?.response?.data?.detail || 'Assignment failed'); }
    finally { setBusy(false); }
  };

  // CURRENT classification is the PERSISTED tenant state (canonical read), never the preview. Preview only
  // supplies the proposed TARGET + delta. Fall back to preview.current only if the canonical read is
  // unavailable (keeps the tab resilient), and to the legacy props for sector/sub_sector as a last resort.
  const persisted = classification && classification.tenant_found !== false ? classification : null;
  const cur = persisted || preview?.current || {};
  const legacySector = persisted && persisted.sector != null ? persisted.sector : sector;
  const legacySubSector = persisted && persisted.sub_sector != null ? persisted.sub_sector : subSector;
  const tgt = preview?.target || {};
  const legacyTotal = (preview?.legacy_appointments_without_profile_context || 0)
    + (preview?.legacy_service_jobs_without_profile_context || 0);

  return (
    <Card data-testid="t360-industry-profile">
      <CardHeader>
        <CardTitle>Industry Profile</CardTitle>
        <CardDescription>Assign/change this tenant's Industry Profile via canonical Industry → Sub-Sector → Profile. Preview is read-only; staff, customers, conversation history and the commercial plan are never changed. The legacy sector label is preserved. Works for any active governed profile.</CardDescription>
      </CardHeader>
      <CardContent className="space-y-3 text-sm">
        <div>
          <div className="text-gray-500 text-xs uppercase">Current classification</div>
          <div>Sector (legacy): <b data-testid="t360ip-sector">{legacySector || '—'}</b></div>
          <div>Sub-sector (legacy): <b data-testid="t360ip-subsector-legacy">{legacySubSector || '—'}</b></div>
          <div>Canonical Industry: <b data-testid="t360ip-current-industry">{cur.industry_id ? nameOf(industries, cur.industry_id) : '— (not mapped)'}</b> · Sub-sector: <b>{cur.sub_sector_id ? nameOf(subSectors, cur.sub_sector_id) : '—'}</b></div>
          <div>Industry Profile: <b data-testid="t360ip-current-profile">{cur.industry_profile_key || '— (none pinned)'}</b>{cur.industry_profile_version ? ` v${cur.industry_profile_version}` : ''}</div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-2 pt-1 border-t">
          <select className="h-8 rounded-md border border-gray-200 bg-white px-2 text-sm" value={industryId}
                  onChange={(e) => { setIndustryId(e.target.value); setSubSectorId(''); setTargetProfileId(''); reset(); }} data-testid="t360ip-industry">
            <option value="">— Industry —</option>
            {industries.map((i) => <option key={i.id} value={i.id}>{i.name}</option>)}
          </select>
          <select className="h-8 rounded-md border border-gray-200 bg-white px-2 text-sm" value={subSectorId}
                  onChange={(e) => { setSubSectorId(e.target.value); setTargetProfileId(''); reset(); }} data-testid="t360ip-subsector" disabled={!industryId}>
            <option value="">— Sub-Sector —</option>
            {subOptions.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
          <select className="h-8 rounded-md border border-gray-200 bg-white px-2 text-sm" value={targetProfileId}
                  onChange={(e) => { setTargetProfileId(e.target.value); reset(); }} data-testid="t360ip-target" disabled={!industryId}>
            <option value="">— Profile —</option>
            {profileOptions.map((p) => <option key={p.id} value={p.id}>{p.display_name} (v{p.version})</option>)}
          </select>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button size="sm" variant="outline" disabled={busy} onClick={runPreview} data-testid="t360ip-preview">Preview Change</Button>
          <Button size="sm" disabled={busy || !canApply} onClick={runApply} data-testid="t360ip-apply">Apply Change</Button>
        </div>

        {err && <p className="text-red-600 text-xs" data-testid="t360ip-error">{err}</p>}
        {preview && !preview.tenant_found && <p className="text-red-600" data-testid="t360ip-no-tenant">Tenant not found.</p>}

        {preview && preview.tenant_found && (
          <div className="space-y-2" data-testid="t360ip-preview-body">
            {noChange ? (
              <div className="text-amber-700" data-testid="t360ip-no-change">
                No change — <b>{tgt.industry_profile_key || cur.industry_profile_key || '—'}</b>{tgt.industry_profile_version ? ` v${tgt.industry_profile_version}` : ''} is already this tenant's assigned profile. Select a different profile to switch. Apply is disabled.
              </div>
            ) : (
              <>
                <div>Target canonical Industry: <b data-testid="t360ip-target-industry">{tgt.industry_id ? nameOf(industries, tgt.industry_id) : '— (profile not mapped to an industry)'}</b> · Sub-sector: <b>{tgt.sub_sector_id ? nameOf(subSectors, tgt.sub_sector_id) : '—'}</b></div>
                <div>Target profile: <b>{tgt.industry_profile_key || '—'}</b>{tgt.industry_profile_version ? ` v${tgt.industry_profile_version}` : ''}</div>
                <div>Capabilities gained: <b data-testid="t360ip-gained">{(preview.capabilities_gained || []).join(', ') || '—'}</b></div>
                <div>Capabilities lost: <b>{(preview.capabilities_lost || []).join(', ') || '—'}</b></div>
                {/* GOV-7 E-5 — governed LOCATION-SOURCE delta (safe labels). movers v2→v3 adds "Registered vehicle
                    tracker" with no engine change, so this is the row that makes the v3 upgrade legible. */}
                <div>Location sources added: <b data-testid="t360ip-sources-gained">{(preview.location_sources_gained || []).join(', ') || '—'}</b></div>
                {(preview.location_sources_lost || []).length > 0 && (
                  <div>Location sources removed: <b data-testid="t360ip-sources-lost">{(preview.location_sources_lost || []).join(', ')}</b></div>
                )}
                <div className="text-xs text-gray-600" data-testid="t360ip-plan-note">Profile enablement is separate from commercial entitlement and role authorization. This assignment does <b>not</b> change the tenant's plan; ETA/workflow still require entitlement + an authorized role.</div>
                {legacyTotal > 0 && (
                  <div className="text-xs text-amber-700 border border-amber-200 rounded p-2" data-testid="t360ip-history-risk">
                    {legacyTotal} existing record(s) were created before Industry-Profile provenance was recorded
                    ({preview.legacy_appointments_without_profile_context || 0} appointment(s), {preview.legacy_service_jobs_without_profile_context || 0} service job(s)).
                    They will <b>not</b> be reclassified, reinterpreted, or backfilled. New records will carry creation-time provenance.
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {result && (
          <div className="mt-2 border-t pt-2" data-testid="t360ip-result">
            <div className="text-green-700 font-medium">Assigned.</div>
            <div className="text-xs text-gray-600">Before: {result.before?.industry_profile_key || '—'} → After: <b>{result.after?.industry_profile_key || '—'}</b> (v{result.after?.industry_profile_version ?? '—'})</div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default Tenant360IndustryProfile;
